<template>
  <div>
    <h2>VALERI Untersuchung</h2>
    <p>
      Die VALERI-Seite bewertet alle Maßnahmen und Maßnahmengruppen eines Projekts
      nach der <b>Kapitalwertmethode (NPV)</b>. Drei Szenarien — Wahrscheinlich,
      Worst-Case und Best-Case — liefern eine Bandbreite der wirtschaftlichen
      Ergebnisse und machen Risiken sichtbar.
    </p>

    <h3>Kurz zum Start</h3>
    <ol>
      <li>Parameter der drei Szenarien oben in der Tabelle prüfen — die Vorgaben sind ein
        brauchbarer Startpunkt.</li>
      <li><b>Werte Speichern</b> drücken. Damit wird gerechnet; vorher zeigen Tabelle und
        Grafiken noch den alten Stand.</li>
      <li>Ergebnis unten ablesen: positiver <b>Kapitalwert</b> heißt, die Maßnahme trägt sich
        über die Laufzeit.</li>
    </ol>

    <h3>Normbezug</h3>
    <p>
      Grundlage ist <b>DIN EN 17463</b> — „Valuation of Energy Related Investments",
      kurz <b>VALERI</b>. Die Norm beschreibt, wie energiebezogene Investitionen
      einheitlich bewertet werden: alle Ein- und Auszahlungen einer Maßnahme werden über
      ihre Laufzeit erfasst und auf den heutigen Zeitpunkt abgezinst. Statt einer einzelnen
      Zahl verlangt sie eine <b>Szenariobetrachtung</b>, weil Energiepreise und Betriebskosten
      über 15 bis 20 Jahre niemand sicher kennt. Daher die drei Spalten.
    </p>

    <h3>Abkürzungen</h3>
    <dl>
      <dt><strong>NPV</strong> (Net Present Value, Kapitalwert)</dt>
      <dd>Summe aller abgezinsten Ein- und Auszahlungen. <b>Größer als 0</b> = die Investition
        lohnt sich gegenüber einer Anlage zum Kalkulationszinssatz.</dd>

      <dt><strong>WACC</strong> (Weighted Average Cost of Capital)</dt>
      <dd>Gewichteter Kapitalkostensatz aus Eigen- und Fremdkapital. Wird als
        Kalkulationszinssatz verwendet, wenn Eigenkapitalanteil und beide Zinssätze gefüllt
        sind; sonst gilt der direkt eingetragene Zinssatz.</dd>

      <dt><strong>IRR</strong> (Internal Rate of Return, interner Zinsfuß)</dt>
      <dd>Der Zinssatz, bei dem der Kapitalwert genau 0 wird — die „Rendite" der Maßnahme.
        Je höher, desto attraktiver.</dd>

      <dt><strong>Annuität</strong></dt>
      <dd>Der Kapitalwert gleichmäßig auf die Jahre der Laufzeit verteilt, in €/a. Macht
        Maßnahmen mit unterschiedlicher Laufzeit vergleichbar.</dd>

      <dt><strong>Dynamische Amortisation</strong></dt>
      <dd>Jahr, in dem die abgezinsten Einnahmen die Investition decken. „Dynamisch", weil
        im Gegensatz zur einfachen Amortisation abgezinst wird.</dd>

      <dt><strong>Restwert</strong></dt>
      <dd>Verbleibender Wert der Anlage am Ende des Betrachtungszeitraums, falls ihre
        Nutzungsdauer länger ist. Er geht abgezinst in den Kapitalwert ein.</dd>

      <dt><strong>Degradation</strong></dt>
      <dd>Nachlassende Wirkung der Maßnahme über die Jahre, z. B. der Leistungsabfall von
        PV-Modulen.</dd>
    </dl>

    <h3>Eingabeparameter</h3>
    <p>Für jedes Szenario können folgende Parameter angepasst werden:</p>
    <table>
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Bedeutung</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Kalkulationszinssatz</td>
          <td>Zinssatz, mit dem künftige Zahlungen abgezinst werden. Wird aus Eigen- und
            Fremdkapital hergeleitet (WACC), sobald Eigenkapitalanteil und beide Zinssätze
            gefüllt sind — sonst gilt der hier eingetragene Wert.</td>
        </tr>
        <tr>
          <td>Energiepreisschwankungen</td>
          <td>Jährliche Steigerung der Energiepreise</td>
        </tr>
        <tr>
          <td>Betriebskostenschwankungen</td>
          <td>Preissteigerung für Dienstleistungen und Material</td>
        </tr>
        <tr>
          <td>Laufzeit der Investition</td>
          <td>Betrachtungszeitraum in Jahren</td>
        </tr>
        <tr>
          <td>Ein-/Auszahlungsberücksichtigung</td>
          <td>Skalierung der Zahlungsströme (Sicherheitsab-/aufschläge)</td>
        </tr>
        <tr>
          <td>Degradation</td>
          <td>Berücksichtigung von Effizienzverlusten über die Lebensdauer</td>
        </tr>
        <tr>
          <td>Nutzungsdauer Fallback</td>
          <td>Standard-Nutzungsdauer, falls keine maßnahmenspezifische angegeben</td>
        </tr>
      </tbody>
    </table>
    <p>
      Über <b>Werte Speichern</b> werden die Parameter projektweit gespeichert
      und alle Szenarien neu berechnet.
    </p>

    <h3>Ergebnistabelle</h3>
    <p>
      Pro Maßnahme / Gruppe werden ausgewiesen:
      <b>Investition</b>, <b>jährl. Energieeinsparung</b>,
      <b>Betriebskostendelta</b>, <b>Kapitalwert (NPV)</b>, <b>Annuität</b>
      und <b>dynamische Amortisation</b>.
      Die Detailansicht kann ein- und ausgeklappt werden.
    </p>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Diagramme</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <dl>
            <dt><strong>Kumulierte Cashflows</strong></dt>
            <dd>
              Zeigt den abgezinsten Zahlungsstrom über die Laufzeit für alle
              drei Szenarien. Der Schnittpunkt mit der x-Achse markiert die
              dynamische Amortisation.
            </dd>

            <dt><strong>NPV-Tornado</strong></dt>
            <dd>
              Horizontale Balken zeigen die Bandbreite des Kapitalwerts je
              Maßnahme zwischen Best- und Worst-Case. Die Markierung zeigt
              das wahrscheinliche Szenario.
            </dd>

            <dt><strong>Cashflow-Stapel</strong></dt>
            <dd>
              Gestapelte Balken der jährlichen Zahlungsströme je Maßnahme
              im aktiven Szenario — zeigt, welche Maßnahmen in welchem Jahr
              den größten Beitrag leisten.
            </dd>

            <dt><strong>Amortisations-Timeline</strong></dt>
            <dd>
              Vergleicht die dynamische Amortisationsdauer aller Maßnahmen
              über die drei Szenarien. Schraffierte Abschnitte zeigen
              Maßnahmen ohne Amortisation im Betrachtungszeitraum.
            </dd>

            <dt><strong>Annuitätenvergleich</strong></dt>
            <dd>
              Gruppierte Balken der jährlichen Annuität je Maßnahme und
              Szenario — erleichtert die Priorisierung.
            </dd>

            <dt><strong>Sensitivitäts-Radar</strong></dt>
            <dd>
              Spinnendiagramm der Szenario-Parameter relativ zum
              wahrscheinlichen Fall. Zeigt, welche Parameter am stärksten
              zwischen den Szenarien variieren.
            </dd>

            <dt><strong>IRR-Vergleich</strong></dt>
            <dd>
              Interner Zinsfuß (IRR) je Maßnahme und Szenario. Maßnahmen
              mit hohem IRR sind besonders attraktiv.
            </dd>
          </dl>
          <p>
            Alle Diagramme können über das Drei-Punkte-Menü (⋮) als PNG
            heruntergeladen oder als Excel-Datei exportiert werden.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Berechnungsgrundlage</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h3>Kapitalwertmethode (NPV)</h3>
          <p>
            Alle zukünftigen Zahlungsströme werden mit dem Kalkulationszins auf
            den Zeitpunkt t=0 abgezinst. Ein positiver NPV bedeutet, dass die
            Investition wirtschaftlich ist.
          </p>

          <h3>Annuität</h3>
          <p>
            Der NPV wird in eine gleichmäßige jährliche Zahlung über die
            Betrachtungsdauer umgerechnet. Sie gibt den jährlichen
            Nettoüberschuss an.
          </p>

          <h3>Dynamische Amortisation</h3>
          <p>
            Zeitpunkt, an dem die kumulierten diskontierten Cashflows erstmals
            positiv werden. Berücksichtigt Zinseffekte, Energiepreissteigerung
            und Degradation.
          </p>

          <h3>IRR – Interner Zinsfuß</h3>
          <p>
            Derjenige Zinssatz, bei dem der NPV exakt null ist. Liegt der IRR
            über dem Kalkulationszins, ist die Maßnahme wirtschaftlich.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="secondary">
          <div class="heading">Hinweise</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ul>
            <li>
              Auf der Seite
              <router-link
                v-if="projectId"
                :to="{ name: 'ProjectDashboard', params: { projectId: String(projectId) } }"
              >Maßnahmen</router-link><span v-else>Maßnahmen</span>
              ausgeschlossene Maßnahmen fließen nicht in die VALERI-Berechnung ein.
            </li>
            <li>
              Die Nutzungsdauer kann pro Maßnahme in der Ergebnistabelle
              individuell überschrieben werden.
            </li>
            <li>
              Worst-/Best-Case-Szenarien sind für Sensitivitätsanalysen gedacht
              — die Standardwerte bilden typische Bandbreiten ab.
            </li>
            <li>
              Die Genauigkeit hängt von den Eingabedaten der einzelnen Maßnahmen
              sowie den gewählten Szenario-Parametern ab.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-footer class="mt-10">
      <small>
        Das Fenster kann an der linken Kante angepasst und über „Hilfe" wieder
        geschlossen werden.
      </small>
    </v-footer>
  </div>
</template>

<script setup lang="ts">
/*
 * `projectId` aus dem Store, damit der Verweis auf die Maßnahmenseite
 * aufgelöst werden kann: die Route `ProjectDashboard` verlangt den Parameter
 * `projectId`. Ohne ihn blieb der `router-link` wirkungslos — derselbe Aufbau
 * wie in `ProjectCalcHelp.vue`.
 */
import { computed } from 'vue';
import { useProjectStore } from '@/stores/projectStore';

const store = useProjectStore();
const projectId = computed(() => store.currentProject?.id ?? null);
</script>

<style scoped></style>
